import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useApi } from "../useApi";
import { isErr } from "../../Utils/Result";
import type { DueDiligenceReportNote } from "../../Types-DTOs/DueDiligence";

interface ReportNotes {
  /** Every note of the case, keyed by the section it is pinned to. */
  notesBySection: Map<number, DueDiligenceReportNote[]>;
  add: (sectionNumber: number, content: string) => Promise<boolean>;
  rewrite: (noteId: string, content: string) => Promise<boolean>;
  remove: (noteId: string) => Promise<boolean>;
}

/**
 * The case's report notes, loaded once and kept in memory. Small enough a
 * collection (a handful per case) that paging or per-section fetching would
 * cost more round trips than it saves.
 *
 * @param caseId case alias or uuid, or undefined while the route resolves
 */
export const useDueDiligenceReportNotes = (caseId: string | undefined): ReportNotes => {
  const {
    listDueDiligenceReportNotesEndpoint,
    createDueDiligenceReportNoteEndpoint,
    updateDueDiligenceReportNoteEndpoint,
    deleteDueDiligenceReportNoteEndpoint,
  } = useApi();

  const [notes, setNotes] = useState<DueDiligenceReportNote[]>([]);

  const listRef = useRef(listDueDiligenceReportNotesEndpoint);
  const createRef = useRef(createDueDiligenceReportNoteEndpoint);
  useEffect(() => { listRef.current = listDueDiligenceReportNotesEndpoint; });
  useEffect(() => { createRef.current = createDueDiligenceReportNoteEndpoint; });
  const updateRef = useRef(updateDueDiligenceReportNoteEndpoint);
  useEffect(() => { updateRef.current = updateDueDiligenceReportNoteEndpoint; });
  const deleteRef = useRef(deleteDueDiligenceReportNoteEndpoint);
  useEffect(() => { deleteRef.current = deleteDueDiligenceReportNoteEndpoint; });

  useEffect(() => {
    setNotes([]);
    if (!caseId) return;

    let cancelled = false;
    listRef.current(caseId).then((result) => {
      if (cancelled || isErr(result)) return;
      setNotes(result.value.notes);
    });
    return () => { cancelled = true; };
  }, [caseId]);

  const add = useCallback(async (sectionNumber: number, content: string) => {
    if (!caseId) return false;
    const result = await createRef.current(caseId, sectionNumber, content);
    if (isErr(result)) return false;
    setNotes((current) => [...current, result.value.note]);
    return true;
  }, [caseId]);

  const rewrite = useCallback(async (noteId: string, content: string) => {
    if (!caseId) return false;
    const result = await updateRef.current(caseId, noteId, content);
    if (isErr(result)) return false;
    const updated = result.value.note;
    setNotes((current) => current.map((note) => (note.id === updated.id ? updated : note)));
    return true;
  }, [caseId]);

  const remove = useCallback(async (noteId: string) => {
    if (!caseId) return false;
    const result = await deleteRef.current(caseId, noteId);
    if (isErr(result)) return false;
    setNotes((current) => current.filter((note) => note.id !== noteId));
    return true;
  }, [caseId]);

  const notesBySection = useMemo(() => {
    const bySection = new Map<number, DueDiligenceReportNote[]>();
    for (const note of notes) {
      const section = bySection.get(note.sectionNumber);
      if (section) section.push(note);
      else bySection.set(note.sectionNumber, [note]);
    }
    // Oldest first: a note reads as a running margin commentary, not a feed.
    for (const section of bySection.values()) {
      section.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    }
    return bySection;
  }, [notes]);

  return { notesBySection, add, rewrite, remove };
};
